import { useCallback, useState } from 'react'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Stack, Typography, Button, Box, Paper } from '@mui/material'
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import {
  useGetTimeTrackingSessionsQuery,
  useGetCurrentTimeTrackingSessionQuery,
  useStartTimeTrackingSessionMutation,
  useForceStartTimeTrackingSessionMutation,
  useStopTimeTrackingSessionMutation,
} from '@/core/api'
import { TimeSession } from '@/core/api/time-tracking'
import { getDeviceId } from '@/features/time-tracking/lib/deviceId'
import { ConflictSessionDialog } from '@/features/time-tracking/ui/ConflictSessionDialog'

import { CorrectSessionDialog, SessionsTable, useTimeTrackingFilters, useTimeTrackingExport, useSessionDialogs } from '@/features/time-tracking'

import { useTimeTrackingAccess } from '../model/useTimeTrackingAccess'
import { SessionsFilter } from './SessionsFilter'
import { SessionsSummary } from './SessionsSummary'
import { TimeTrackingDisabled } from './TimeTrackingDisabled'

export function TimeTrackingPage() {
  useLingui()

  const { isAdmin, isConsentLoading, canAccessPage } = useTimeTrackingAccess()
  const { from, to, pagination, setFrom, setTo, setPagination } = useTimeTrackingFilters()
  const handleExport = useTimeTrackingExport({ from, to })
  const { correctSession, openCorrectDialog, closeCorrectDialog } = useSessionDialogs()

  const { data: currentSession, refetch: refetchCurrentSession } = useGetCurrentTimeTrackingSessionQuery(undefined, { skip: isAdmin || !canAccessPage })
  const [startSession, { isLoading: isStarting }] = useStartTimeTrackingSessionMutation()
  const [forceStartSession, { isLoading: isForceStarting }] = useForceStartTimeTrackingSessionMutation()
  const [stopSession, { isLoading: isStopping }] = useStopTimeTrackingSessionMutation()
  const [conflictSession, setConflictSession] = useState<TimeSession | null>(null)
  const isSessionActive = !!currentSession?.isOpen

  const handleStartSession = async () => {
    try {
      const result = await startSession({ deviceId: getDeviceId() }).unwrap()
      if (result.conflict) {
        setConflictSession(result.existingSession)
      } else {
        refetchCurrentSession()
      }
    } catch {}
  }

  const handleForceStartSession = useCallback(async () => {
    try {
      await forceStartSession({ deviceId: getDeviceId() }).unwrap()
      setConflictSession(null)
      refetchCurrentSession()
    } catch {}
  }, [forceStartSession, refetchCurrentSession])

  const handleStopSession = async () => {
    try {
      await stopSession().unwrap()
      refetchCurrentSession()
    } catch {}
  }

  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const { data, isFetching } = useGetTimeTrackingSessionsQuery(
    {
      from: from.toISOString(),
      to: to.toISOString(),
      skip: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    },
    { skip: isAdmin || !canAccessPage }
  )

  if (isConsentLoading) {
    return (
      <Stack flex={1} alignItems="center" justifyContent="center" sx={{ minHeight: '50vh' }}>
        <HeartbeatLoader sx={{ transform: 'scale(4)' }} />
      </Stack>
    )
  }

  if (!canAccessPage) {
    return <TimeTrackingDisabled />
  }

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100%' }}>
      <Box sx={{ maxWidth: 1440, mx: 'auto', px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'flex-start' }} justifyContent="space-between" spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: -0.5 }}>
                <Trans>Time Tracking</Trans>
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  <Trans>View your recorded working hours and sessions</Trans>
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              {!isAdmin && (
                isSessionActive ? (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<StopRoundedIcon />}
                    onClick={handleStopSession}
                    disabled={isStopping}
                    sx={{ height: 44, px: 2.5, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, whiteSpace: 'nowrap' }}
                  >
                    <Trans>Stop session</Trans>
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<PlayArrowRoundedIcon />}
                    onClick={handleStartSession}
                    disabled={isStarting}
                    sx={{ height: 44, px: 2.5, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, whiteSpace: 'nowrap' }}
                  >
                    <Trans>Start session</Trans>
                  </Button>
                )
              )}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadRoundedIcon />}
                onClick={handleExport}
                sx={{ height: 44, px: 2.5, textTransform: 'none', fontWeight: 600, borderRadius: 1.5, whiteSpace: 'nowrap' }}
              >
                <Trans>Export CSV</Trans>
              </Button>
            </Stack>
          </Stack>

          {data && <SessionsSummary totals={data.totals} openSessionCount={data.openSessionCount} />}

          <SessionsFilter from={from} to={to} onFromChange={setFrom} onToChange={setTo} />

          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', borderRadius: 2, overflow: 'hidden' }}>
            <SessionsTable
              key={`${from.toISOString()}-${to.toISOString()}`}
              sessions={data?.items ?? []}
              loading={isFetching}
              totalRows={data?.totalCount ?? 0}
              onPaginationChange={setPagination}
              onCorrect={openCorrectDialog}
            />
          </Paper>

          <CorrectSessionDialog open={!!correctSession} session={correctSession} onClose={closeCorrectDialog} />
          <ConflictSessionDialog
            session={conflictSession}
            loading={isForceStarting}
            onContinue={handleForceStartSession}
            onCancel={() => setConflictSession(null)}
          />
        </Stack>
      </Box>
    </Box>
  )
}
